import { brand } from "../../../config/theme";
import { isMenuItemEnabled } from "../../../config/appsConfig";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useUserStore } from "../../../store/userStore";
import { useActiveAppStore } from "../../../store/activeAppStore";
import { useSessionCheck } from "../../../hooks/useSessionCheck";
import { faStore } from "@fortawesome/free-solid-svg-icons";
import { setLoggingOut } from "../../../utils/tokenUtils";
import { logoutUser } from "../../../pages/login/functions";

import { Header } from "../../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../../components/base/AppsSidebar";
import { useTheme } from "../../../context/ThemeContext";
import type { AppItem } from "../../../components/base/AppsSidebar";
import Swal from "sweetalert2";

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  route: string;
  dividerAfter?: boolean;
}

export default function POSEcommerceLayout() {
  const { t } = useTranslation("common");
  const { themeMode, isDarkMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);

  const {
    isExpiringSoon,
    timeRemainingFormatted,
    isExpired,
    isAuthenticated,
    isInitialized,
  } = useSessionCheck({
    warningThreshold: 60 * 60 * 1000,
    autoRefresh: true,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const setActiveApp = useActiveAppStore((state) => state.setActiveApp);
  useEffect(() => {
    setActiveApp(
      "pos-ecommerce",
      t("apps.pos-ecommerce.name"),
      t("apps.pos-ecommerce.description"),
      faStore,
      "bg-violet-500"
    );
  }, [setActiveApp, t]);

  useEffect(() => {
    if (location.pathname === "/pos/ecommerce" || location.pathname === "/pos/ecommerce/") {
      navigate("/pos/ecommerce/dashboard", { replace: true });
    }
  }, [location.pathname, navigate]);

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);
  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.id === "pos-ecommerce") {
      closeAppsSidebar();
      return;
    }
    
    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    
    if (app.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { name: app.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    
    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank", "noopener,noreferrer");
    }
  };

  useEffect(() => {
    if (
      isInitialized &&
      (isExpired || !isAuthenticated) &&
      !hasRedirected.current
    ) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user) {
      if (location.pathname !== "/splash") {
        localStorage.setItem("lastRoute", location.pathname);
        navigate("/splash");
      }
    }
  }, [user, location.pathname, navigate]);

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
    sidebarBg: isDarkMode ? "bg-dark-surface" : "bg-white",
  };

  const menuItems: MenuItem[] = [
    {
      id: "dashboard",
      label: t("posMenu.storeDashboard"),
      icon: "ri-dashboard-line",
      route: "/pos/ecommerce/dashboard",
    },
    {
      id: "storefront",
      label: t("posMenu.storefront"),
      icon: "ri-store-2-line",
      route: "/pos/ecommerce/storefront",
      dividerAfter: true,
    },
    {
      id: "pages",
      label: t("posMenu.storePages"),
      icon: "ri-pages-line",
      route: "/pos/ecommerce/pages",
    },
    {
      id: "theme",
      label: t("posMenu.storeTheme"),
      icon: "ri-palette-line",
      route: "/pos/ecommerce/theme",
    },
    {
      id: "navigation",
      label: t("posMenu.navigation"),
      icon: "ri-menu-line",
      route: "/pos/ecommerce/navigation",
      dividerAfter: true,
    },
    {
      id: "shipping",
      label: t("posMenu.shipping"),
      icon: "ri-truck-line",
      route: "/pos/ecommerce/shipping",
    },
    {
      id: "payments",
      label: t("posMenu.paymentGateways"),
      icon: "ri-bank-card-line",
      route: "/pos/ecommerce/payments",
    },
    {
      id: "settings",
      label: t("posMenu.storeSettings"),
      icon: "ri-settings-3-line",
      route: "/pos/ecommerce/settings",
    },
  ];

  // Filter menu items based on app config
  const filteredMenuItems = menuItems.filter((item) =>
    isMenuItemEnabled("point-of-sale", "pos-ecommerce", item.id),
  );

  return (
    <div className={`h-screen flex flex-col ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}>
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>
            {t("sessionBanner.expiring", { time: timeRemainingFormatted })}
          </span>
        </div>
      )}

      <Header 
        onOpenAppsSidebar={openAppsSidebar}
        onToggleMobileMenu={toggleMobileMenu}
        isMobileMenuOpen={isMobileMenuOpen}
        isDarkMode={isDarkMode}
      />
      
      <div className="flex flex-1 min-h-0 overflow-hidden relative">
        {isMobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={closeMobileMenu}
          />
        )}

        <div
          className={`
            ${themeClasses.sidebarBg} border-r ${themeClasses.border} shadow-sm
            fixed lg:relative inset-y-0 left-0 z-50
            w-72 h-full min-h-0 transform transition-transform duration-300 ease-in-out
            ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
            lg:block overflow-y-auto
          `}
        >
          <div className="p-4 sm:p-6">
            <div className={`mb-4 p-3 rounded-xl border bg-violet-500/10 border-violet-500/30`}>
              <div className="flex items-center space-x-2">
                <i className="ri-global-line text-lg text-violet-500"></i>
                <div className="flex-1">
                  <p className="text-xs font-medium text-violet-500">
                    {t("posMenu.storeStatus")}
                  </p>
                  <p className={`text-sm font-semibold ${themeClasses.text}`}>
                    {t("posMenu.online")}
                  </p>
                </div>
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
              </div>
            </div>

            <nav className="space-y-1">
              {filteredMenuItems.map((item) => {
                const isActive = location.pathname.includes(item.id);
                return (
                  <div key={item.id}>
                    <button
                      onClick={() => {
                        navigate(item.route);
                        closeMobileMenu();
                      }}
                      className={`w-full flex items-center p-2.5 sm:p-3 rounded-xl transition-all text-left ${
                        isActive
                          ? "bg-primary-500 text-gray-900 shadow-lg"
                          : `${themeClasses.buttonText} ${themeClasses.buttonTextHover} ${themeClasses.buttonHover}`
                      }`}
                    >
                      <i className={`${item.icon} text-lg mr-3 flex-shrink-0`}></i>
                      <span className="font-medium text-sm sm:text-base truncate">{item.label}</span>
                    </button>
                    {item.dividerAfter && (
                      <div className={`my-2 border-t ${themeClasses.border}`} />
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </div>

        <div className="flex-1 max-h-full overflow-hidden min-w-0 w-full">
          <Outlet />
        </div>
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId="pos-ecommerce"
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        isDarkMode={isDarkMode}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={user ? {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          username: user.username,
        } : undefined}
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
